import { observable, makeObservable, action, runInAction } from 'mobx';
import { api } from '../../constants/apis';
import { deleteRequest, get, post, put } from '../../utils/axios-requests';
import filterContacts from './contacts.utils';

export type Contact = {
  image: string;
  NamePrefix: any;
  FirstName: any;
  MiddleName: any;
  LastName: any;
  NameSuffix: any;
  Nickname: any;
  JobTitle: any;
  Department: any;
  Company: any;
  profileCode: any;
  profileQR: any;
  isPublic: true;
  isBasic: true;
  Number: any;
  Website: any;
  messageNumber: any;
  Location: any;
  Email: any;
  Field: any;
  verified: boolean;
  allowFollowing: boolean;
  allowSyncing: boolean;
  socials: [];
  allowAlias: boolean;
  _id: string;
  name: string;
  userID: string;
  connections: number;
  followers: number;
  syncs: number;
  favorite: boolean;
  tags: Array<string>;
  connected: boolean;
  followed: boolean;
  langs: Array<string>;
};

export default class ContactsUiStore {
  @observable error: string = '';
  @observable context: any = null;
  @observable loading: boolean = false;
  @observable allContacts: { [key: string]: Array<Contact> } | null = null;
  @observable contacts: { [key: string]: Array<Contact> } | null = null;
  @observable card: string = '';
  @observable query: string = '';
  @observable userCards: Array<any> = [];
  @observable languages: Array<string> = [];
  @observable tags: Array<string> = [];

  constructor() {
    makeObservable(this);
  }

  @action
  public initliazeContext = (context: any) => {
    this.context = context;
  };

  @action
  public setError = (err: string) => {
    this.error = err;
  };

  public fetchContacts = async (filters: any, selectedProfile: string) => {
    try {
      const res =
        selectedProfile === 'All Cards' || !selectedProfile
          ? await post(api.connections.user, { filters })
          : await post(api.connections.userCard, { filters }, {cardId: selectedProfile});

      runInAction(() => {
        this.contacts = res.data;
        this.allContacts = res.data;
      });
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  public getLabelAndDefaultCard = async (cardId: string) => {
    try {
      const res = await get(api.connections.label, {
        userId: this.context.authenticatedUser.id,
        cardId,
      });

      const res2 = await get(api.connections.default, {
        userId: this.context.authenticatedUser.id,
        cardId,
      });

      return { label: res.data, card: res2.data };
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  public updateLabel = async (cardId: string, label: string, img: string) => {
    try {
      await put(api.connections.label, { label, img }, { cardId });
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  public setDefaultCard = async (cardId: string) => {
    try {
      await post(api.connections.default, {}, { cardId });
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  public removeCard = async (cardId: string) => {
    try {
      await deleteRequest(api.connections.card, { cardId });
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public updateFollow = async (follow: boolean, cardId: string) => {
    try {
      if (follow)
        await post(api.connections.follow, {
          cardRequested: cardId,
        });
      else
        await deleteRequest(api.connections.unfollow, {
          cardId,
        });
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public sendSyncRequest = async (
    cards: Array<string>,
    message: string,
    cardId: string
  ) => {
    try {
      const res = await post(api.connections.requests, {
        cardRequested: cardId,
        cards,
        message,
      });

      return res.data;
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  public fetchCards = async () => {
    try {
      const res = await get(api.cards.user);
      runInAction(() => {
        this.userCards = res.data;
      });
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  public fetchSettings = async () => {
    try {
      await this.fetchAllLanguages();
      const res = await get(api.users.settings);
      return res.data;
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  public updateSettings = async (settings: any) => {
    try {
      await put(api.users.settings, settings);
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public fetchAllLanguages = async () => {
    try {
      const res = await get(api.languages.all);

      runInAction(() => {
        this.languages = res.data;
      });
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public toggleFavirote = async (favorite: boolean, cardId: string) => {
    try {
      await post(api.connections.favorite, { favorite }, { cardId });
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public getUserTags = async () => {
    try {
      const res = await get(api.connections.tags);

      runInAction(() => {
        this.tags = res.data;
      });
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public getAllTags = async () => {
    try {
      const res = await get(api.tags.all);

      return res.data;
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public updateConnectionTags = async (tags: Array<string>, cardId: string) => {
    try {
      this.tags = this.tags.concat(tags.filter((i) => !this.tags.includes(i)));
      await put(api.connections.connectionTags, { tags }, { cardId });
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public deleteTag = async (tagId: string) => {
    try {
      await deleteRequest(api.tags.single, { tagId });

      return this.getAllTags();
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public updateTag = async (tagId: string, value: string) => {
    try {
      await put(api.tags.single, { tag: value }, { tagId });

      return this.getAllTags();
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public filterContacts = async (query: string) => {
    if (query) {
      const data = await filterContacts(query, this.allContacts);
      runInAction(() => {
        this.contacts = data;
      });
    } else {
      this.contacts = this.allContacts;
    }
  };

  @action
  public getContactsFilters = async () => {
    try {
      const res = await get(api.connections.filters);

      return res.data;
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }
      return false;
    }
  };
}
